import * as React from 'react';
import {useEffect, useMemo, useState} from 'react';
import {graphql, useMutation} from 'react-relay';
import {ArrowLeft, ArrowRight, Check} from 'lucide-react';
import {Draft, Host, STEP_LABEL, StepKey, canContinue, clearDraft, emptyDraft, loadDraft, saveDraft, stepsFor, toInput, uploading} from './draft';
import {ContactStep, CrewStep, DetailsStep, GearStep, ModelsStep, ScanStep, WhatStep} from './steps';
import {EngagementBuilderSubmitMutation} from './__generated__/EngagementBuilderSubmitMutation.graphql';

const SUBMIT = graphql`
    mutation EngagementBuilderSubmitMutation($input: CreateEngagementInput!) {
        createEngagement(input: $input) {
            engagement { id }
            userErrors { message }
        }
    }`;

type Props = {
    host: Host;
    // Public host: draft is persisted across navigation and the sign-in round trip.
    initialDraft?: Partial<Draft>;
    signedInAs?: string | null;
    // Ops host: the customer step is rendered by the host (it owns the pickers); builder gates on `customerId`.
    customerId?: string;
    renderCustomerStep?: () => React.ReactNode;
    onSubmitted: (engagementId: string, draft: Draft) => void;
};

// One stepper for every way an engagement gets built. Hosts decide who it's for;
// the draft, steps, validation and submit payload are shared (see draft.ts).
const EngagementBuilder: React.FC<Props> = ({host, initialDraft, signedInAs, customerId, renderCustomerStep, onSubmitted}) => {
    const persist = host === 'public';
    const [draft, setDraft] = useState<Draft>(() => {
        const saved = persist ? loadDraft() : null;
        const base = saved ?? emptyDraft();
        // A deep link's preselection wins over an old saved draft's interests.
        return initialDraft?.interests?.length ? {...base, ...initialDraft} : {...base, ...initialDraft, interests: base.interests};
    });
    const [stepIndex, setStepIndex] = useState(0);
    const [error, setError] = useState<string | null>(null);
    const [commit, inFlight] = useMutation<EngagementBuilderSubmitMutation>(SUBMIT);

    useEffect(() => { if (persist) saveDraft(draft); }, [draft, persist]);

    const update = (patch: Partial<Draft>) => setDraft((d) => ({...d, ...patch}));
    const steps = useMemo(() => stepsFor(draft, host), [draft, host]);
    const current: StepKey = steps[Math.min(stepIndex, steps.length - 1)];
    const last = stepIndex >= steps.length - 1;
    const ok = canContinue(current, draft, Boolean(customerId));

    const submit = () => {
        setError(null);
        commit({
            variables: {input: toInput(draft, {host, customerId}) as any},
            onCompleted: (res) => {
                const payload = res.createEngagement;
                if (payload?.engagement) {
                    if (persist) clearDraft();
                    onSubmitted(payload.engagement.id, draft);
                } else {
                    setError(payload?.userErrors.map((u) => u.message).join('; ') ?? 'Something went wrong');
                }
            },
            onError: (err) => setError(err.message),
        });
    };

    return (
        <div className="max-w-3xl mx-auto">
            <ol className="flex flex-wrap items-center gap-3 mb-8 text-xs uppercase tracking-widest">
                {steps.map((key, i) => (
                    <li key={key} className={`flex items-center gap-2 ${i === stepIndex ? 'text-white' : i < stepIndex ? 'text-lumina-accent' : 'text-gray-600'}`}>
                        <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${i < stepIndex ? 'bg-lumina-accent text-black' : i === stepIndex ? 'bg-lumina-purple text-white' : 'border border-gray-700'}`}>
                            {i < stepIndex ? <Check className="w-3 h-3"/> : i + 1}
                        </span>
                        {STEP_LABEL[key]}
                        {i < steps.length - 1 && <span className="w-6 h-px bg-gray-800"/>}
                    </li>
                ))}
            </ol>

            <div className="bg-[#0a0a0f] border border-gray-800 p-8 md:p-10 rounded-2xl">
                {current === 'customer' && renderCustomerStep?.()}
                {current === 'what' && <WhatStep draft={draft} update={update}/>}
                {current === 'gear' && <GearStep draft={draft} update={update}/>}
                {current === 'models' && <ModelsStep draft={draft} update={update}/>}
                {current === 'scan' && <ScanStep draft={draft} update={update}/>}
                {current === 'crew' && <CrewStep draft={draft} update={update}/>}
                {current === 'details' && <DetailsStep draft={draft} update={update}/>}
                {current === 'contact' && <ContactStep draft={draft} update={update} signedInAs={signedInAs}/>}

                {error && <p className="text-red-400 text-sm mt-4">{error}</p>}

                <div className="flex items-center justify-between mt-8">
                    <button type="button" onClick={() => setStepIndex((s) => Math.max(0, s - 1))} disabled={stepIndex === 0}
                            className="flex items-center gap-2 text-xs font-bold tracking-widest uppercase text-gray-400 hover:text-white disabled:opacity-30">
                        <ArrowLeft className="w-4 h-4"/> Back
                    </button>
                    {!last ? (
                        <button type="button" onClick={() => setStepIndex((s) => s + 1)} disabled={!ok}
                                className="flex items-center gap-2 px-6 py-3 bg-lumina-accent text-black text-xs font-bold tracking-widest uppercase hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed">
                            {current === 'models' && uploading(draft) ? 'Uploading…' : 'Continue'} <ArrowRight className="w-4 h-4"/>
                        </button>
                    ) : (
                        <button type="button" onClick={submit} disabled={!ok || inFlight}
                                className="px-6 py-3 bg-gradient-to-r from-lumina-accent to-lumina-purple text-white font-display font-bold tracking-widest hover:opacity-90 disabled:opacity-40">
                            {host === 'ops' ? 'FILE ENGAGEMENT' : 'SEND IT'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default EngagementBuilder;
